import PeriodsService from "@/services/PeriodsService";
import { removeItemFromList, getListItems, getListPagination } from "@/utils/listHelpers";
import { getApiErrorMessage, notifyStoreError } from "@/utils/storeHelpers";

const belongsToSelectedOrganization = (organizationId, selectedOrganizationId) => {
    if (!selectedOrganizationId) return true;

    return Number(organizationId) === Number(selectedOrganizationId);
};

export default {
    namespaced: true,
    state: {
        periodsData: [],
        period: [],
        newPeriod: [],
        status: null,
    },
    getters: {
        getPeriodsData: (state) => getListItems(state.periodsData),
        getPeriodsPagination: (state) => getListPagination(state.periodsData),
        getPeriodData: (state) => state.period,
        getNewPeriodData: (state) => state.newPeriod,
        getStatus: (state) => state.status,
    },
    mutations: {
        setPeriodsData(state, data) {
            state.periodsData = data;
        },
        setPeriodData(state, data) {
            state.period = data;
        },
        setNewPeriodData(state, data) {
            state.newPeriod = data;
        },
        setStatus(state, status) {
            state.status = status;
        },
        createPeriodInList(state, period) {
            if (!period) return;

            if (Array.isArray(state.periodsData)) {
                state.periodsData.push(period);
            } else if (state.periodsData?.data && Array.isArray(state.periodsData.data)) {
                state.periodsData.data.push(period);
            }
        },
    },
    actions: {
        async getPeriods({ commit, dispatch }, payload) {
            try {
                const response = await PeriodsService.getPeriods(payload.data, payload.userToken);
                commit('setPeriodsData', response);
            } catch (error) {
                console.error('Erro ao buscar períodos', error);
                commit('setStatus', getApiErrorMessage(error, 'Erro ao buscar períodos'));
            } finally {
                if (payload.skeletonKey) {
                    dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true })
                }
            }
        },
        async createPeriod({ commit, dispatch, rootGetters }, payload) {
            try {
                const response = await PeriodsService.createPeriod(payload.period, payload.userToken);
                const selectedOrganizationId = rootGetters['organizations/getSelectedOrganizationId'];

                if (!belongsToSelectedOrganization(payload.period.organization_id, selectedOrganizationId)) {
                    dispatch('toastfy/activeInfoNotification', {
                        text: 'Período criado com sucesso em outra organização. Altere o filtro para visualizá-lo.'
                    }, { root: true });

                    return response;
                }

                commit('createPeriodInList', response.data);
                dispatch('toastfy/activeSuccesNotification', { text: 'Período adicionado com sucesso' }, { root: true });

                return response;
            } catch (error) {
                notifyStoreError(dispatch, 'Erro ao adicionar período', error);
                throw error;
            }
        },
        async getPeriod({ commit }, payload) {
            try {
                const response = await PeriodsService.getPeriodById(payload.data, payload.userToken);
                commit('setPeriodData', response.data);
            } catch (error) {
                console.error('Erro ao buscar período', error);
                commit('setStatus', getApiErrorMessage(error, 'Erro ao buscar período'));
            }
        },
        async updatePeriod({ commit, state, dispatch, rootGetters }, payload) {
            try {
                const response = await PeriodsService.updatePeriod(payload.period, payload.userToken);
                const updatedPeriod = response.data;
                const selectedOrganizationId = rootGetters['organizations/getSelectedOrganizationId'];

                if (!belongsToSelectedOrganization(payload.period.organization_id, selectedOrganizationId)) {
                    commit('setPeriodsData', removeItemFromList(state.periodsData, updatedPeriod.id));
                    dispatch('toastfy/activeInfoNotification', {
                        text: 'Período movido para outra organização. Altere o filtro para visualizá-lo.'
                    }, { root: true });

                    return response;
                }

                dispatch('toastfy/activeSuccesNotification', { text: "Período atualizado com sucesso" }, { root: true });

                if (Array.isArray(state.periodsData)) {
                    const newList = state.periodsData.map(p => p.id === updatedPeriod.id ? { ...p, ...updatedPeriod } : p);
                    commit('setPeriodsData', newList);
                }
                else if (state.periodsData?.data && Array.isArray(state.periodsData.data)) {
                    const newList = state.periodsData.data.map(p => p.id === updatedPeriod.id ? { ...p, ...updatedPeriod } : p);
                    commit('setPeriodsData', {
                        ...state.periodsData,
                        data: newList
                    });
                }

                return response;
            } catch (error) {
                notifyStoreError(dispatch, 'Erro ao atualizar período', error);
                throw error;
            }
        },
        async deletePeriod({ commit, state, dispatch }, payload) {
            try {
                await PeriodsService.deletePeriod(payload.data, payload.userToken);

                dispatch('toastfy/activeSuccesNotification', { text: "Período deletado com sucesso" }, { root: true });

                let updatedPeriodsData = null;
                const periodId = payload.data;

                if (Array.isArray(state.periodsData)) {
                    updatedPeriodsData = state.periodsData.filter(p => p.id !== periodId);
                } else if (state.periodsData && Array.isArray(state.periodsData.data)) {
                    updatedPeriodsData = {
                        ...state.periodsData,
                        data: state.periodsData.data.filter(p => p.id !== periodId)
                    };
                }

                if (updatedPeriodsData) {
                    commit('setPeriodsData', updatedPeriodsData);
                }
            } catch (error) {
                notifyStoreError(dispatch, 'Erro ao deletar período', error);
            }
        },
    }
}
